import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Menu,
  Calendar,
  MapPin,
  CheckCircle2,
  Users,
  FileText,
  ClipboardList,
  Settings,
  User,
  Bell,
  AlertTriangle,
  Database,
  History,
  ChevronLeft,
  LogOut,
  Sun,
  Moon,
  Eye,
  Shield,
  Layers,
  Mail,
  BookUser,
  Clock,
  CalendarCheck,
  DollarSign,
  Plane,
  Trophy,
} from "lucide-react";
import { useTheme } from "next-themes";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { NotificationsSidebar } from "@/components/NotificationsSidebar";
import { useEnvironment } from "@/contexts/EnvironmentContext";
import { usePageMetadata } from "@/contexts/PageMetadataContext";
import { useCoachView } from "@/contexts/CoachViewContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

interface HeaderProps {
  show?: boolean;
  isAdmin?: boolean;
  isCoach?: boolean;
  isAssistant?: boolean;
}

export function Header({
  show = false,
  isAdmin = false,
  isCoach = false,
  isAssistant = false,
}: HeaderProps) {
  const navigate = useNavigate();
  const { showDevData } = useEnvironment();
  const { metadata } = usePageMetadata();
  const { theme, setTheme } = useTheme();
  const { isAdmin: canUseCoachView, viewAsCoach, toggleViewMode } = useCoachView();
  const coachViewLabel = viewAsCoach ? "Admin View" : "Coach View";

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  if (!show) {
    return null;
  }

  const mainItems = [{ title: "Dashboard", url: "/dashboard", icon: Calendar }];

  const coachItems = isAssistant && !isCoach && !isAdmin
    ? [
        { title: "Sessions", url: "/sessions", icon: Calendar },
        { title: "Planners", url: "/planners", icon: CalendarCheck },
        { title: "Timesheets", url: "/timesheets", icon: Clock },
        { title: "Holidays", url: "/holidays", icon: Plane },
        { title: "My Documents", url: "/coach-documents", icon: FileText },
      ]
    : [
        { title: "Attendance", url: "/attendance", icon: ClipboardList },
        { title: "Planners", url: "/planners", icon: CalendarCheck },
        { title: "Tournaments", url: "/tournaments", icon: Trophy },
        { title: "Timesheets", url: "/timesheets", icon: Clock },
        { title: "Holidays", url: "/holidays", icon: Plane },
        { title: "My Documents", url: "/coach-documents", icon: FileText },
      ];

  const managementItems = [
    { title: "Venues", url: "/venues", icon: MapPin },
    { title: "Sessions", url: "/sessions", icon: Calendar },
    { title: "Bulk Sessions", url: "/bulk-sessions", icon: Layers },
    {
      title: "Attendance Approval",
      url: "/attendance-approval",
      icon: CheckCircle2,
    },
    { title: "Unpaid Attendance", url: "/unpaid-attendance", icon: DollarSign },
    { title: "Timesheet Approvals", url: "/timesheets/approvals", icon: Clock },
    { title: "Planner Manager", url: "/planners/admin", icon: CalendarCheck },
    { title: "Holidays", url: "/holidays", icon: Plane },
    { title: "Tournament Approvals", url: "/tournaments/approvals", icon: Trophy },
    { title: "Participant Book", url: "/participant-book", icon: BookUser },
    { title: "Incidents", url: "/incidents", icon: AlertTriangle },
    { title: "Mailshot", url: "/mailshot", icon: Mail },
    { title: "Coach Documents", url: "/coach-documents", icon: FileText },
    { title: "Audit Log", url: "/audit-log", icon: History },
  ];

  const configItems = [
    { title: "Users", url: "/users", icon: Users },
    { title: "Data Sync", url: "/data-sync", icon: Database },
    { title: "Settings", url: "/settings", icon: Settings },
  ];

  return (
    <header className="sticky top-0 z-50 flex h-14 items-center bg-sidebar gap-2 border-b border-accent bg-background dark:bg-[hsl(210,9%,15%)] dark:shadow-[0_5px_15px_7px_rgba(0,0,0,0.4)]  shadow-[0_0px_20px_10px_rgba(0,0,0,0.2)] px-4">
      {/* Back button - only shown when metadata.showBackButton is true */}
      {metadata?.showBackButton && metadata?.onBackClick && (
        <Button
          variant="ghost"
          size="icon"
          onClick={metadata.onBackClick}
          className="h-8 w-8 flex-shrink-0"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
      )}

      {/* Logo */}
      <img
        src="/logo_light.webp"
        alt="MicroSports Logo"
        className="block dark:hidden h-8 w-auto"
      />
      <img
        src="/logo_dark.webp"
        alt="MicroSports Logo"
        className="hidden dark:block h-8 w-auto"
      />
      {showDevData && (
        <Badge
          variant="outline"
          className="bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700 ml-2"
        >
          DEV
        </Badge>
      )}

      {/* Menu Button */}
      <div className="flex ml-auto items-center gap-2">
        {/* Notifications - aligned right */}
        <NotificationsSidebar>
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-5 w-5" />
            <span className="sr-only">Notifications</span>
          </Button>
        </NotificationsSidebar>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-6 w-6" />
              <span className="sr-only">Open navigation menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
              Main
            </DropdownMenuLabel>
            {mainItems.map((item) => (
              <DropdownMenuItem
                key={item.title}
                onClick={() => navigate(item.url)}
              >
                <item.icon className="mr-2 h-4 w-4" />
                {item.title}
              </DropdownMenuItem>
            ))}

            {(isCoach || isAdmin || isAssistant) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                  Coach
                </DropdownMenuLabel>
                {coachItems.map((item) => (
                  <DropdownMenuItem
                    key={item.title}
                    onClick={() => navigate(item.url)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                  </DropdownMenuItem>
                ))}
              </>
            )}

            {isAdmin && !viewAsCoach && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                  Management
                </DropdownMenuLabel>
                {managementItems.map((item) => (
                  <DropdownMenuItem
                    key={item.title}
                    onClick={() => navigate(item.url)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                  </DropdownMenuItem>
                ))}
              </>
            )}

            {isAdmin && !viewAsCoach && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                  Configuration
                </DropdownMenuLabel>
                {configItems.map((item) => (
                  <DropdownMenuItem
                    key={item.title}
                    onClick={() => navigate(item.url)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                  </DropdownMenuItem>
                ))}
              </>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/profile")}>
              <User className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            {canUseCoachView && (
              <DropdownMenuItem onClick={toggleViewMode}>
                {viewAsCoach ? (
                  <Shield className="mr-2 h-4 w-4" />
                ) : (
                  <Eye className="mr-2 h-4 w-4" />
                )}
                {coachViewLabel}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? (
                <Sun className="mr-2 h-4 w-4" />
              ) : (
                <Moon className="mr-2 h-4 w-4" />
              )}
              {theme === "dark" ? "Light Mode" : "Dark Mode"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
